import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import backarrowbtn from "../../../assets/images/backarrowbtn.svg";
import { PrivacyData } from "./PrivacySectionData";

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState<string>("overview");

  // ✔ FULLY DYNAMIC REFS (1–100 sections)
  const sectionRefs = useMemo(() => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
    PrivacyData.sections.forEach((sec) => {
      refs[sec.id] = React.createRef<HTMLDivElement>();
    });
    return refs;
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    sectionRefs[id]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const activeIndex = useMemo(() => {
    return PrivacyData.sections.findIndex((sec) => sec.id === activeSection);
  }, [activeSection]);

  useEffect(() => {
    const container = document.querySelector(".scroll-container");

    const handleScroll = () => {
      let currentId = activeSection;

      for (const sec of PrivacyData.sections) {
        const ref = sectionRefs[sec.id]?.current;
        if (ref) {
          const rect = ref.getBoundingClientRect();

          // Normal section detection
          if (rect.top <= 200 && rect.bottom >= 200) {
            currentId = sec.id;
            break;
          }
        }
      }

      // Detect when user reaches very bottom → activate LAST section
      if (
        container &&
        container.scrollTop + container.clientHeight >= container.scrollHeight - 10
      ) {
        currentId = PrivacyData.sections[PrivacyData.sections.length - 1].id;
      }

      if (currentId !== activeSection) {
        setActiveSection(currentId);
      }
    };

    container?.addEventListener("scroll", handleScroll);
    return () => container?.removeEventListener("scroll", handleScroll);
  }, [activeSection, sectionRefs]);


  return (
    <div className="flex flex-col md:flex-row w-full h-screen p-4 gap-4 bg-[#f9f6ff]">

      <div className="relative w-full md:w-1/3 lg:w-1/4 bg-white border border-[#D4D4D4] rounded-xl p-4 overflow-y-auto h-full">
        <div className="flex-wrap font-medium text-[17px] xl:text-[20px] text-black flex items-center gap-3 mb-3 ">
          <Link to="/">
            <span className="cursor-pointer">
              <img src={backarrowbtn} alt="Back Image" className="w-6 h-6" />
            </span>
          </Link>
          Waivera’s Privacy Policy
        </div>
        <div className="flex justify-center w-full mt-2 mb-3.5">
          <div className="border-b w-[90%] "></div>
        </div>

        <div className="relative flex flex-col items-start pt-4">
          {/* Gray full line */}
          <div className="absolute top-5 bottom-0 left-[7px] w-[2px] bg-[#D4D4D4]" />

          {/* Purple line ONLY after first index */}
          {activeIndex > 0 && (
            <div
              className="absolute top-5 left-[7px] w-[2px] bg-[#7E57C2]"
              style={{
                height:
                  activeIndex === PrivacyData.sections.length - 1
                    ? "calc(100% - 20px)" // stop before bottom
                    : `${activeIndex * 48 + 8}px`, // perfect alignment
              }}
            />
          )}

          {PrivacyData.sections.map((sec, index) => (
            <div key={sec.id} className="relative flex items-center gap-4 py-2">
              <div className="min-w-5 min-h-4">
                <div
                  className={[
                    "w-3 h-3 rounded-full ml-[2px]",
                    index <= activeIndex ? "bg-[#7E57C2]" : "bg-[#D4D4D4]",
                  ].join(" ")}
                />
              </div>

              <span
                className={[
                  "cursor-pointer text-[15px] xl:text-[16px]",
                  activeIndex === index
                    ? "text-[#7E57C2] font-semibold"
                    : "text-[#6D6D6D]",
                ].join(" ")}
                onClick={() => scrollToSection(sec.id)}
              >
                {sec.title}
              </span>
            </div>
          ))}
        </div>

      </div>

      <div className="w-full md:w-2/3 lg:w-3/4 overflow-y-auto h-full scroll-smooth scroll-container">
        {PrivacyData.sections.map((sec) => (
          <div
            key={sec.id}
            ref={sectionRefs[sec.id]}
            className="mb-10 scroll-mt-20 bg-white border border-[#D4D4D4] rounded-xl p-4 xl:p-6"
          >
            <h2 className="text-[20px] xl:text-[25px] font-medium leading-[46px] text-black mb-2.5">
              {sec.title}
            </h2>

            {sec.content}
          </div>
        ))}
      </div>
    </div>
  );
}


